// src/pages/UserReservationsPage.js
import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import useUserReservations from '../hooks/useUserReservations';
import { CalendarDays, Clock, Users, Dice5, Frown, ArrowRight } from 'lucide-react';
import Config from "../Config.json";
import ICON from "../assets/icons/favicon.ico";

const TITLE = `${Config.SITE_TITLE} - Minhas Reservas`;

// Formata a data de YYYY-MM-DD para DD/MM/YYYY
function formatToBrDate(dateString) {
    if (!dateString) return "Data não informada";
    if (dateString.includes("-")) {
        const [year, month, day] = dateString.split("-");
        return `${day}/${month}/${year}`;
    }
    return dateString;
}

function UserReservationsPage() {
    const { user, loading } = useContext(AuthContext);
    const navigate = useNavigate();

    // Consumindo a API através do nosso Hook
    const { reservations, loadingReservations, errorReservations } = useUserReservations(user);

    useEffect(() => {
        if (!loading && !user) {
            navigate("/login");
        }
        window.scrollTo(0, 0); // Rola para o topo ao abrir a página
    }, [user, loading, navigate]);

    if (loading || !user) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-[#FFF5EE]">
                <div className="w-12 h-12 border-4 border-[#dbcdac] border-t-[#528a89] rounded-full animate-spin mb-4"></div>
                <p className="text-[#528a89] font-serif text-lg animate-pulse">Carregando...</p>
            </div>
        );
    }

    // === NOVA LÓGICA DE ORDENAÇÃO ===
    // Ordena as reservas para que as mais recentes fiquem no topo
    const sortedReservations = [...reservations].sort((a, b) => {
        const dateA = a.createdAt || a.CreatedAt;
        const dateB = b.createdAt || b.CreatedAt;

        if (dateA && dateB) {
            return new Date(dateB) - new Date(dateA);
        }
        
        // Fallback: se não tiver data de criação, ordena pelo ID maior (mais recente)
        const idA = a.id || a.ID || 0;
        const idB = b.id || b.ID || 0;
        return idB - idA;
    });

    return (
        <div className="min-h-screen bg-[#FFF5EE] pt-24 pb-32 relative overflow-hidden font-sans text-stone-800">
            <title>{TITLE}</title>
            <link rel="icon" href={ICON} />

            {/* Textura de Fundo Vintage */}
            <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/aged-paper.png')]" />

            <div className="max-w-4xl mx-auto px-4 md:px-6 relative z-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
                
                <div className="mb-10 text-center md:text-left border-b border-[#dbcdac]/40 pb-6">
                    <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#971253]">
                        Minhas Reservas
                    </h1>
                    <p className="mt-2 text-[#528a89] font-light text-lg">
                        O histórico completo das suas aventuras.
                    </p>
                </div>

                <div className="space-y-6">
                    {/* ESTADO 1: Carregando */}
                    {loadingReservations && (
                        <div className="flex flex-col items-center justify-center py-20 text-center bg-white/50 backdrop-blur-sm border border-[#dbcdac]/40 rounded-[2rem]">
                            <div className="w-10 h-10 border-4 border-[#dbcdac] border-t-[#528a89] rounded-full animate-spin mb-4"></div>
                            <p className="text-stone-500 font-medium">Buscando do acervo...</p>
                        </div>
                    )}

                    {/* ESTADO 2: Erro */}
                    {!loadingReservations && errorReservations && (
                        <div className="flex flex-col items-center justify-center py-16 text-center bg-red-50 border border-red-200 rounded-[2rem]">
                            <p className="text-red-600 font-medium mb-4">{errorReservations}</p>
                            <button onClick={() => window.location.reload()} className="px-6 py-2 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-colors">
                                Tentar novamente
                            </button>
                        </div>
                    )}

                    {/* ESTADO 3: Lista Vazia */}
                    {!loadingReservations && !errorReservations && sortedReservations.length === 0 && (

                        <div className="flex flex-col items-center justify-center py-24 text-center bg-white/60 backdrop-blur-sm rounded-[2rem] border border-dashed border-[#dbcdac]/60 shadow-sm">
                            <div className="w-20 h-20 bg-[#528a89]/10 rounded-full flex items-center justify-center mb-6">
                                <Frown size={40} className="text-[#528a89]" />
                            </div>
                            <h3 className="font-serif text-3xl text-[#971253] mb-3">Nenhuma mesa reservada</h3>
                            <p className="text-stone-500 max-w-sm mb-8 font-light text-lg">
                                O salão está vazio. Que tal convocar a sua turma e agendar a próxima jogatina?
                            </p>
                            <button 
                                onClick={() => navigate('/reserva')} 
                                className="px-8 py-4 bg-[#528a89] hover:bg-[#3a6362] text-[#FFF5EE] font-bold tracking-wider rounded-xl transition-all flex items-center gap-2 shadow-lg hover:-translate-y-1"
                            >
                                <Dice5 size={20} /> Fazer uma Reserva
                            </button>
                        </div>
                    )}

                    {/* ESTADO 4: Lista de Reservas (Renderização com array ORDENADO) */}
                    {!loadingReservations && !errorReservations && sortedReservations.map((res) => {
                        const type = res.type || res.Type || "Reserva";
                        const isRPG = type.toUpperCase() === 'RPG';
                        const date = formatToBrDate(res.date || res.Data);
                        const time = res.time || res.Time;
                        const id = res.id || res.ID;
                        
                        // Extração segura do nome dos participantes, pois a API retorna um Array de Objetos
                        const participantsArray = res.participants || res.Participants || [];
                        const peopleCount = participantsArray.length || res.guests || res.people || 1;

                        return (
                            <div key={id} className="bg-white/80 backdrop-blur-sm rounded-[2rem] border border-[#dbcdac]/40 shadow-sm p-6 md:p-8 hover:shadow-[0_8px_20px_-10px_rgba(82,138,137,0.2)] hover:border-[#528a89]/40 transition-all duration-300">
                                
                                {/* 1. Header Section */}
                                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6 border-b border-[#dbcdac]/30 pb-5">
                                    <div className="flex flex-wrap items-center gap-3">
                                        <span className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${isRPG ? 'bg-[#971253]/10 text-[#971253]' : 'bg-[#528a89]/10 text-[#528a89]'}`}>
                                            {type}
                                        </span>
                                        <div className="flex items-center text-sm text-stone-600 gap-1.5 font-medium">
                                            <CalendarDays size={16} className="text-[#dbcdac]" /> {date}
                                            <span className="mx-1 text-stone-300">•</span>
                                            <Clock size={16} className="text-[#dbcdac]" /> {time}
                                        </div>
                                    </div>
                                    
                                    {/* Oculta a badge de quantidade de pessoas se for RPG usando "invisible" para manter o alinhamento */}
                                    <div className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-bold tracking-wide w-max ${isRPG ? 'invisible' : 'bg-stone-100 text-stone-500'}`}>
                                        <Users size={14} /> {peopleCount} {peopleCount > 1 ? 'pessoas' : 'pessoa'}
                                    </div>
                                </div>

                                {/* 2. Participants Preview Section */}
                                <div className="mb-8">
                                    <p className="text-xs font-bold text-[#528a89] uppercase tracking-widest mb-3">
                                        Integrantes da Mesa
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {participantsArray.slice(0, 3).map((p, index) => {
                                            const pName = typeof p === 'object' ? p.name : p;
                                            return (
                                                <span key={index} className="bg-[#FFF5EE] border border-[#dbcdac]/40 text-stone-600 px-3 py-1.5 rounded-lg text-sm font-medium shadow-sm">
                                                    {pName}
                                                </span>
                                            );
                                        })}
                                        {participantsArray.length > 3 && (
                                            <span className="bg-[#528a89]/10 text-[#528a89] px-3 py-1.5 rounded-lg text-sm font-bold">
                                                +{participantsArray.length - 3}
                                            </span>
                                        )}
                                        {participantsArray.length === 0 && (
                                            <span className="text-stone-400 text-sm italic">Participantes não informados</span>
                                        )}
                                    </div>
                                </div>

                                {/* 3. Footer Section */}
                                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-4 border-t border-[#dbcdac]/30">
                                    <span className="text-sm font-bold text-stone-400 w-full sm:w-auto text-center sm:text-left">
                                        Reserva #{id}
                                    </span>
                                    <button 
                                        onClick={() => navigate(`/reserva/${id}`)} 
                                        className="w-full sm:w-auto bg-[#528a89] hover:bg-[#3a6362] text-[#FFF5EE] font-bold tracking-wider px-8 py-3 rounded-xl transition-all shadow-md hover:-translate-y-0.5 flex items-center justify-center gap-2"
                                    >
                                        Detalhes <ArrowRight size={18} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default UserReservationsPage;